import { addElement, animate, nap, px, spring, stageSize, type DemoImpl, type Point } from '@/components/motion/demos/demoKit'

/* Chapter 2: the deal team scans the market, a book is chosen with you, financed and closed, and it
   joins your firm. */

/* Market firms around your firm: [angle in degrees, distance as a share of the stage's half-size].
   The chosen book sits on the left, so the route reads left to right: you choose, finance, close,
   your firm. */
const market: Point[] = [
  [-20, 0.8], [-58, 0.92], [-96, 0.88], [192, 0.98], [-80, 0.6], [156, 0.92],
  [128, 0.84], [80, 0.92], [46, 0.8], [12, 0.92], [-16, 0.7], [-42, 0.58],
]
const found = [2, 3, 6]
const chosen = 3
const svgNamespace = 'http://www.w3.org/2000/svg'

type RadarContext = {
  stage: HTMLElement
  sweep: HTMLElement
  path: SVGPathElement
  nodes: HTMLElement[]
  firm: HTMLElement
  cluster: HTMLElement[]
  newDots: HTMLElement[]
  youLabel: HTMLElement
  financeLabel: HTMLElement
  closeLabel: HTMLElement
  firmLabel: HTMLElement
}

type RadarGeometry = {
  width: number
  height: number
  firm: Point
  nodes: Point[]
  chosen: Point
  route: string
}

const geometry = (context: RadarContext): RadarGeometry => {
  const { width, height } = stageSize(context.stage)
  const firm: Point = [width / 2, height / 2 + 6]
  const rx = Math.min(width / 2 - 34, 250)
  const ry = height / 2 - 30
  const nodes = market.map(([angle, share]): Point => {
    const radians = (angle * Math.PI) / 180
    return [firm[0] + Math.cos(radians) * rx * share, firm[1] + Math.sin(radians) * ry * share]
  })
  const book = nodes[chosen]
  const vx = firm[0] - book[0]
  const vy = firm[1] - book[1]
  const length = Math.hypot(vx, vy) || 1
  const ux = vx / length
  const uy = vy / length
  let nx = -uy
  let ny = ux
  // The route bows upward, away from the label under the chosen book.
  if (ny > 0) {
    nx = -nx
    ny = -ny
  }
  const end = [firm[0] - ux * 36, firm[1] - uy * 36]
  const c1 = [book[0] + vx * 0.35 + nx * 34, book[1] + vy * 0.35 + ny * 34]
  const c2 = [book[0] + vx * 0.72 + nx * 18, book[1] + vy * 0.72 + ny * 18]
  const route = `M ${book[0]} ${book[1]} C ${c1[0]} ${c1[1]}, ${c2[0]} ${c2[1]}, ${end[0]} ${end[1]}`
  return { width, height, firm, nodes, chosen: book, route }
}

const onRing = (center: Point, index: number, count: number, radius: number): Point => {
  const angle = (index / count) * Math.PI * 2 - Math.PI / 2
  return [center[0] + Math.cos(angle) * radius, center[1] + Math.sin(angle) * radius]
}

const place = (context: RadarContext, g: RadarGeometry, done: boolean) => {
  context.nodes.forEach((node, index) => {
    node.style.left = px(g.nodes[index][0])
    node.style.top = px(g.nodes[index][1])
    node.className = done && found.includes(index) ? 'm-node found' : 'm-node'
    node.style.opacity = done && index === chosen ? '0.35' : '1'
  })
  Object.assign(context.firm.style, { left: px(g.firm[0]), top: px(g.firm[1]), transform: done ? 'scale(1.12)' : 'none' })
  context.cluster.forEach((dot, index) => {
    const [x, y] = onRing(g.firm, index, 8, 46)
    dot.style.left = px(x)
    dot.style.top = px(y)
  })
  context.newDots.forEach((dot, index) => {
    const [x, y] = onRing(g.firm, index + 0.5, 5, 60)
    dot.style.transform = done ? `translate(${x}px, ${y}px)` : `translate(${g.chosen[0]}px, ${g.chosen[1]}px)`
    dot.style.opacity = done ? '1' : '0'
  })
  const sweep = Math.ceil(2 * Math.hypot(Math.max(g.firm[0], g.width - g.firm[0]), Math.max(g.firm[1], g.height - g.firm[1]))) + 24
  Object.assign(context.sweep.style, {
    left: px(g.firm[0]),
    top: px(g.firm[1]),
    width: px(sweep),
    height: px(sweep),
    margin: `${-sweep / 2}px 0 0 ${-sweep / 2}px`,
    opacity: '0',
  })
  context.path.setAttribute('d', g.route)
  context.path.style.strokeDashoffset = done ? '0' : '1'
  context.path.style.opacity = done ? '0.35' : '1'
  const length = context.path.getTotalLength()
  const finance = context.path.getPointAtLength(length * 0.32)
  const close = context.path.getPointAtLength(length * 0.7)
  Object.assign(context.financeLabel.style, { left: px(finance.x), top: px(finance.y) })
  Object.assign(context.closeLabel.style, { left: px(close.x), top: px(close.y) })
  Object.assign(context.youLabel.style, { left: px(g.chosen[0] + 16), top: px(g.chosen[1] + 26) })
  Object.assign(context.firmLabel.style, { left: px(g.firm[0]), top: px(g.firm[1] + 70) })
  for (const label of [context.financeLabel, context.closeLabel, context.youLabel, context.firmLabel]) {
    label.style.opacity = done ? '1' : '0'
  }
}

export const radarDemo: DemoImpl<RadarContext> = {
  setup: (stage) => {
    const sweep = addElement('div', 'm-sweep', stage)
    const route = document.createElementNS(svgNamespace, 'svg')
    route.setAttribute('class', 'm-route')
    stage.appendChild(route)
    const path = document.createElementNS(svgNamespace, 'path')
    path.setAttribute('pathLength', '1')
    route.appendChild(path)
    const nodes = market.map(() => addElement('div', 'm-node', stage))
    const firm = addElement('div', 'm-firm', stage, 'Your<br>firm')
    const cluster = Array.from({ length: 8 }, () => addElement('div', 'm-cdot', stage))
    const newDots = Array.from({ length: 5 }, () => addElement('div', 'm-cdot new', stage))
    const youLabel = addElement('span', 'm-flabel you', stage, 'You choose')
    const financeLabel = addElement('span', 'm-flabel', stage, 'Finance')
    const closeLabel = addElement('span', 'm-flabel', stage, 'Close')
    const firmLabel = addElement('span', 'm-firm-label', stage, 'Your firm grows')
    return { stage, sweep, path, nodes, firm, cluster, newDots, youLabel, financeLabel, closeLabel, firmLabel }
  },
  reset: (context) => place(context, geometry(context), false),
  final: (context) => place(context, geometry(context), true),
  play: async (context, ok) => {
    const g = geometry(context)
    animate(
      context.sweep,
      [{ opacity: 0, transform: 'rotate(-40deg)' }, { opacity: 1, offset: 0.15 }, { opacity: 1, offset: 0.8 }, { opacity: 0, transform: 'rotate(320deg)' }],
      { duration: 2000, easing: 'linear' },
    )
    for (const [step, index] of found.entries()) {
      await nap(step === 0 ? 450 : 380)
      ok()
      context.nodes[index].classList.add('found')
      animate(context.nodes[index], [{ transform: 'scale(1)' }, { transform: 'scale(1.7)' }, { transform: 'scale(1)' }], { duration: 420, easing: spring })
    }
    await nap(650)
    ok()
    context.nodes[chosen].classList.add('chosen')
    animate(
      context.youLabel,
      [{ opacity: 0, transform: 'translate(-50%, -30%) scale(.9)' }, { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' }],
      { duration: 380, easing: spring },
    )
    await nap(850)
    ok()
    const draw = animate(context.path, [{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }], { duration: 1000, easing: 'cubic-bezier(.5,0,.2,1)' })
    animate(context.financeLabel, [{ opacity: 0 }, { opacity: 1 }], { duration: 300, delay: 320 })
    animate(context.closeLabel, [{ opacity: 0 }, { opacity: 1 }], { duration: 300, delay: 700 })
    await draw.finished
    ok()
    const length = context.path.getTotalLength()
    const points = Array.from({ length: 12 }, (_, step) => context.path.getPointAtLength((length * step) / 11))
    context.newDots.forEach((dot, index) => {
      const [x, y] = onRing(g.firm, index + 0.5, 5, 60)
      const keyframes: Keyframe[] = points.map((point) => ({ transform: `translate(${point.x}px, ${point.y}px)`, opacity: 1 }))
      keyframes.push({ transform: `translate(${x}px, ${y}px)`, opacity: 1 })
      keyframes[0].opacity = 0
      animate(dot, keyframes, { duration: 1300, delay: index * 140, easing: 'cubic-bezier(.4,0,.2,1)' })
    })
    animate(context.nodes[chosen], [{ opacity: 1 }, { opacity: 0.35 }], { duration: 800, delay: 600 })
    await nap(1300 + 4 * 140 - 150)
    ok()
    animate(context.firm, [{ transform: 'scale(1)' }, { transform: 'scale(1.18)' }, { transform: 'scale(1.12)' }], { duration: 600, easing: spring })
    animate(context.path, [{ opacity: 1 }, { opacity: 0.35 }], { duration: 600 })
    animate(
      context.firmLabel,
      [{ opacity: 0, transform: 'translate(-50%, 4px)' }, { opacity: 1, transform: 'translate(-50%, 0)' }],
      { duration: 400, delay: 200 },
    )
  },
}
